import { useState } from 'react';
import {
  Box,
  Heading,
  Input,
  Stack,
  Text,
  Link as ChakraLink,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useDocs } from './useDocs';
import { useAdminDocsHost } from './host-context';
import type { DocCategory } from './types';

const CATEGORY_LABELS: Record<DocCategory, string> = {
  sso: 'Single Sign-On',
  directory: 'Directory & Provisioning',
  operations: 'Operations',
  reference: 'Reference',
};

export interface DocsListProps {
  /** Optional initial category filter. */
  category?: DocCategory;
}

export function DocsList({ category }: DocsListProps) {
  const [query, setQuery] = useState('');
  const { docs, totalCount } = useDocs({ category, query });
  const { resolveDocUrl } = useAdminDocsHost();

  const grouped = docs.reduce<Record<string, typeof docs>>((acc, doc) => {
    (acc[doc.category] ??= []).push(doc);
    return acc;
  }, {});

  return (
    <Box maxW="container.md" mx="auto" px="4" py="6">
      <Heading as="h1" size="lg" mb="2">
        Admin guides
      </Heading>
      <Text color="neutral.600" mb="4">
        Setup runbooks for tenant administrators.
      </Text>
      <Input
        placeholder="Search guides…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        mb="6"
      />
      {totalCount === 0 && (
        <Text color="neutral.500">No guides match your search.</Text>
      )}
      <Stack gap="6">
        {Object.entries(grouped).map(([cat, items]) => (
          <Box key={cat}>
            <Heading as="h2" size="md" mb="3">
              {CATEGORY_LABELS[cat as DocCategory] ?? cat}
            </Heading>
            <Stack gap="3">
              {items.map((doc) => (
                <Box
                  key={doc.slug}
                  p="4"
                  borderWidth="1px"
                  borderColor="neutral.200"
                  borderRadius="md"
                >
                  <ChakraLink asChild fontWeight="semibold" color="blue.600">
                    <RouterLink
                      to={resolveDocUrl ? resolveDocUrl(doc.slug) : `./${doc.slug}`}
                    >
                      {doc.title}
                    </RouterLink>
                  </ChakraLink>
                  <Text color="neutral.600" mt="1">
                    {doc.summary}
                  </Text>
                </Box>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
